// Shared photo grid + lightbox, extracted from OutingPage and reused for the
// community photos on route pages (F23). The lightbox closes on Escape,
// backdrop click, or the close button; the optional per-photo `link` renders
// under the caption in both grid and lightbox (route photos link to their
// source outing).
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { X } from "lucide-react";
import { useT } from "../lib/i18n";
import { photoUrl, thumbUrl } from "../lib/photos";

export type GalleryPhoto = {
  id: string;
  storageKey: string;
  caption: string | null;
  link?: { href: string; label: string };
};

export function PhotoGallery({ photos }: { photos: GalleryPhoto[] }) {
  const { t } = useT();
  const [lightbox, setLightbox] = useState<GalleryPhoto | null>(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {photos.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => setLightbox(p)}
              className="block w-full overflow-hidden rounded-xl border shadow-card transition-all duration-150 hover:shadow-card-hover"
            >
              <img src={thumbUrl(p.storageKey, 640, 640)} alt={p.caption ?? ""} loading="lazy" className="aspect-square w-full object-cover" />
            </button>
            {p.caption && <p className="mt-1 truncate text-xs text-muted-foreground">{p.caption}</p>}
            {p.link && (
              <Link
                href={p.link.href}
                className="mt-0.5 block truncate text-xs text-primary transition-colors hover:underline"
              >
                {p.link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.caption ?? t("photo.label")}
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/90 p-4"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label={t("photo.closeAria")}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X aria-hidden className="size-5" />
          </button>
          <img
            src={photoUrl(lightbox.storageKey)}
            alt={lightbox.caption ?? ""}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-pop"
          />
          {(lightbox.caption || lightbox.link) && (
            <div onClick={(e) => e.stopPropagation()} className="max-w-xl text-center text-sm">
              {/* Fixed dark scrim: literal light colors on purpose — semantic
                  tokens would flip unreadable in light theme. */}
              {lightbox.caption && <p className="text-white/90">{lightbox.caption}</p>}
              {lightbox.link && (
                <Link href={lightbox.link.href} className="text-alpine-300 hover:text-alpine-200 hover:underline">
                  {lightbox.link.label}
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
